import React from 'react';
import { Chip, alpha } from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ReplayIcon from '@mui/icons-material/Replay';
import { OrderPaymentStatus } from '../../types';
import { palette, typography, theme as themeConfig } from '../../theme/colors';

interface PaymentStatusChipProps {
  status: OrderPaymentStatus;
  size?: 'small' | 'medium';
}

interface StatusConfig {
  color: string;
  label: string;
  icon: React.ReactElement;
}

const getPaymentStatusConfig = (status: OrderPaymentStatus): StatusConfig => {
  switch (status) {
    case OrderPaymentStatus.UNPAID:
      return {
        color: palette.error,
        label: 'NON PAYÉ',
        icon: <HourglassEmptyIcon sx={{ fontSize: 16 }} />,
      };
    case OrderPaymentStatus.PARTIALLY_PAID:
      return {
        color: palette.warning,
        label: 'PARTIELLEMENT PAYÉ',
        icon: <WarningIcon sx={{ fontSize: 16 }} />,
      };
    case OrderPaymentStatus.PAID:
      return {
        color: palette.success,
        label: 'PAYÉ',
        icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
      };
    case OrderPaymentStatus.REFUNDED_PARTIAL:
      return {
        color: palette.info,
        label: 'REMBOURSÉ PARTIELLEMENT',
        icon: <ReplayIcon sx={{ fontSize: 16 }} />,
      };
    case OrderPaymentStatus.REFUNDED_FULL:
      return {
        color: palette.textMuted,
        label: 'REMBOURSÉ',
        icon: <ReplayIcon sx={{ fontSize: 16 }} />,
      };
    default:
      return {
        color: palette.textMuted,
        label: String(status).toUpperCase(),
        icon: <PaymentIcon sx={{ fontSize: 16 }} />,
      };
  }
};

export const PaymentStatusChip: React.FC<PaymentStatusChipProps> = ({
  status,
  size = 'small'
}) => {
  const config = getPaymentStatusConfig(status);

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size={size}
      sx={{
        backgroundColor: alpha(config.color, 0.1),
        color: config.color,
        fontFamily: typography.fontFamily.mono,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.bold,
        border: `1px solid ${config.color}`,
        textTransform: 'uppercase',
        borderRadius: themeConfig.radius.sm,
        '& .MuiChip-icon': {
          color: config.color,
        },
      }}
    />
  );
};

export default PaymentStatusChip;
